"use client";

import {
  AdvancedList,
  Button,
  LoadingSpinner,
  type AdvancedListSortDef,
} from "@west-tennessee-consulting/ui";
import { RotateCw } from "lucide-react";
import type { ClientFolder } from "./SaveAttachmentsToGoogleDriveModal.types";

interface ClientFolderPickerProps {
  clientFolders: ClientFolder[];
  isRefreshing: boolean;
  listingLoading: boolean;
  onRefreshFolders: () => void;
  onSelectClient: (folder: ClientFolder) => void;
  selectedClient: ClientFolder | null;
}

const SORTS: Array<AdvancedListSortDef<ClientFolder>> = [
  { id: "name", label: "Name", compare: (a, b) => a.name.localeCompare(b.name) },
];

/**
 * Search + sort via the shared AdvancedList primitive (packages/ui) — the
 * root Drive folder listing here is fetched at pageSize:1000, so this can
 * genuinely be hundreds of client folders with previously no sort at all.
 * Search moved from parent-controlled state into AdvancedList's own internal
 * state (was previously filtered in useSaveAttachmentsToGoogleDriveState).
 */
export function ClientFolderPicker({
  clientFolders,
  isRefreshing,
  listingLoading,
  onRefreshFolders,
  onSelectClient,
  selectedClient,
}: ClientFolderPickerProps) {
  return (
    <>
      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={onRefreshFolders}
          disabled={listingLoading || isRefreshing}
          title="Refresh folder list"
          className={isRefreshing ? "animate-spin" : ""}
        >
          {listingLoading || isRefreshing ? (
            <LoadingSpinner className="h-4 w-4" />
          ) : (
            <RotateCw className="h-4 w-4" />
          )}
        </Button>
      </div>

      <div className="border rounded-lg p-2">
        <AdvancedList<ClientFolder>
          items={clientFolders}
          getId={(folder) => folder.id}
          searchFields={(folder) => [folder.name]}
          searchPlaceholder="Search clients…"
          sorts={SORTS}
          defaultSortId="name"
          maxHeight="12rem"
          emptyState="No client folders found"
          renderItem={(folder) => (
            <button
              type="button"
              onClick={() => onSelectClient(folder)}
              className={`w-full text-left px-4 py-2 rounded hover:bg-muted transition-colors text-sm ${
                selectedClient?.id === folder.id ? "bg-primary/10 border-l-2 border-primary" : ""
              }`}
            >
              <div className="font-medium">{folder.name}</div>
            </button>
          )}
        />
      </div>
    </>
  );
}
